import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const testimonials = [
  {
    id: 1,
    name: "Sarah Johnson",
    role: "Marketing Director, TechCorp",
    content: "The animation videos we received were beyond our expectations. They perfectly captured our brand identity and helped us increase engagement by 300%.",
    avatar: "https://randomuser.me/api/portraits/women/44.jpg",
    rating: 5
  },
  {
    id: 2,
    name: "Michael Chen",
    role: "Founder, StartupX",
    content: "Incredible quality at an affordable price. The team was responsive and delivered exactly what we needed for our product launch.",
    avatar: "https://randomuser.me/api/portraits/men/32.jpg",
    rating: 5
  },
  {
    id: 3,
    name: "Emily Rodriguez",
    role: "Content Manager, EduPlus",
    content: "Our educational animations have never looked better. The attention to detail and creative approach made complex concepts easy to understand.",
    avatar: "https://randomuser.me/api/portraits/women/68.jpg",
    rating: 4
  },
  {
    id: 4,
    name: "David Okafor",
    role: "Head of Growth, Loomly",
    content: "We've used three different animation studios before Eunbro. None of them matched the turnaround time or the polish we got here.",
    avatar: "https://randomuser.me/api/portraits/men/61.jpg",
    rating: 5
  },
  {
    id: 5,
    name: "Priya Nair",
    role: "Product Marketer, Fintra",
    content: "The explainer video helped us cut support tickets in half because customers finally understood the product before signing up.",
    avatar: "https://randomuser.me/api/portraits/women/21.jpg",
    rating: 5
  },
  {
    id: 6,
    name: "Tom Whitfield",
    role: "Creative Director, Anvil Studio",
    content: "As a fellow creative, I'm picky about animation quality. Eunbro's work is genuinely some of the best I've commissioned.",
    avatar: "https://randomuser.me/api/portraits/men/12.jpg",
    rating: 4
  },
];

const getInitials = (name) =>
  name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

const Avatar = ({ name, src }) => {
  const [failed, setFailed] = useState(false);

  if (failed || !src) {
    return (
      <div
        className="w-12 h-12 rounded-full bg-indigo-100 text-indigo-700 font-semibold flex items-center justify-center flex-shrink-0"
        aria-hidden="true"
      >
        {getInitials(name)}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={name}
      loading="lazy"
      onError={() => setFailed(true)}
      className="w-12 h-12 rounded-full object-cover flex-shrink-0"
    />
  );
};

const TestimonialCard = ({ testimonial, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.2 }}
    transition={{ duration: 0.5, delay: (index % 3) * 0.1, ease: 'easeOut' }}
    className="bg-white p-8 rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300"
  >
    <div className="flex items-center mb-6">
      <Avatar name={testimonial.name} src={testimonial.avatar} />
      <div className="ml-4">
        <h4 className="font-bold text-gray-900">
          {testimonial.name}
        </h4>
        <p className="text-sm text-gray-600">
          {testimonial.role}
        </p>
      </div>
    </div>

    <p className="text-gray-600 mb-6 italic">
      "{testimonial.content}"
    </p>

    <div className="flex items-center" aria-label={`Rated ${testimonial.rating} out of 5 stars`}>
      {[...Array(5)].map((_, i) => (
        <svg
          key={i}
          aria-hidden="true"
          className={`w-5 h-5 ${i < testimonial.rating ? 'text-yellow-400' : 'text-gray-300'}`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
      <span className="ml-2 text-sm text-gray-600">
        {testimonial.rating}.0 rating
      </span>
    </div>
  </motion.div>
);

const Testimonials = () => {
  const [showAll, setShowAll] = useState(false);
  const visibleTestimonials = showAll ? testimonials : testimonials.slice(0, 3);

  return (
    <section id="reviews" className="py-20 bg-gray-50 scroll-mt-20">
      <div className="container mx-auto px-6 lg:px-8">
        <motion.div
          className="max-w-4xl mx-auto text-center mb-16"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          <span className="inline-block bg-indigo-100 text-indigo-800 text-sm font-semibold px-4 py-1 rounded-full mb-4">
            Client Testimonials
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            What Our <span className="text-indigo-600">Clients Say</span>
          </h2>
          <p className="text-lg text-gray-600">
            Don't just take our word for it - hear from businesses that have transformed their content with our animations.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <AnimatePresence initial={false}>
            {visibleTestimonials.map((testimonial, index) => (
              <TestimonialCard key={testimonial.id} testimonial={testimonial} index={index} />
            ))}
          </AnimatePresence>
        </div>

        {testimonials.length > 3 && (
          <div className="flex justify-center mt-12">
            <button
              type="button"
              onClick={() => setShowAll((prev) => !prev)}
              className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-indigo-500"
            >
              {showAll ? 'Show Fewer Reviews' : 'Read More Reviews'}
              <svg
                className={`ml-2 -mr-1 w-5 h-5 transition-transform duration-300 ${showAll ? 'rotate-180' : ''}`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default Testimonials;
